import { useEffect, useState } from 'react';
import { Users, Plus, Trash2, Edit, X, Check } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface AdminUser { id: string; email: string; role: string; name: string | null; created_at: number; last_login: number | null; is_active: number; }

const ROLES = ['super_admin', 'editor', 'viewer'];
const ROLE_LABEL: Record<string, string> = { super_admin: '👑 Super Admin', editor: '✏️ Editor', viewer: '👁️ Viewer' };
const ROLE_COLOR: Record<string, string> = {
  super_admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  editor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  viewer: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};
const inputCls = 'w-full px-3 py-2 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm';

export default function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState({ email: '', password: '', role: 'editor', name: '' });
  const [editForm, setEditForm] = useState({ role: '', name: '', password: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    const res = await apiFetch('/api/admin/users');
    const data = await res.json();
    setUsers(data.users || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email || !form.password) { setError('Email and password required'); return; }
    setSaving(true); setError('');
    const res = await apiFetch('/api/admin/users', { method: 'POST', body: JSON.stringify(form) });
    if (res.ok) { setShowNew(false); setForm({ email: '', password: '', role: 'editor', name: '' }); load(); }
    else { const d = await res.json(); setError(d.error || 'Failed'); }
    setSaving(false);
  };

  const startEdit = (u: AdminUser) => {
    setEditing(u.id);
    setEditForm({ role: u.role, name: u.name || '', password: '', is_active: u.is_active === 1 });
  };

  const saveEdit = async (id: string) => {
    setSaving(true);
    const body: Record<string, unknown> = { role: editForm.role, name: editForm.name, is_active: editForm.is_active };
    if (editForm.password) body.password = editForm.password;
    await apiFetch(`/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    setEditing(null); setSaving(false); load();
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this admin user?')) return;
    await apiFetch(`/api/admin/users/${id}`, { method: 'DELETE' });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">Admin Users</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage who can access this admin panel</p>
        </div>
        <button onClick={() => { setShowNew(true); setError(''); }} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl text-sm font-medium shadow hover:opacity-90 transition">
          <Plus size={15} /> Add Admin
        </button>
      </div>

      {/* New user form */}
      {showNew && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-indigo-200/60 dark:border-indigo-700/40 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">New Admin User</h2>
            <button onClick={() => setShowNew(false)}><X size={18} className="text-gray-400" /></button>
          </div>
          {error && <p className="text-sm text-red-500 mb-3">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Email</label><input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inputCls} placeholder="admin@example.com" /></div>
            <div><label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Password</label><input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className={inputCls} placeholder="Strong password…" /></div>
            <div><label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Display name" /></div>
            <div><label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className={inputCls}>
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setShowNew(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button onClick={create} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">{saving ? 'Creating…' : 'Create User'}</button>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 overflow-hidden">
        {loading ? <div className="py-12 text-center text-gray-400">Loading…</div> : users.length === 0 ? (
          <div className="py-16 text-center text-gray-400"><Users size={40} className="mx-auto mb-3 opacity-30" /><p>No admin users yet.</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/80 dark:bg-gray-700/30">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">User</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Role</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Created</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Last Login</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/50 dark:hover:bg-gray-700/20">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">{(u.name || u.email)[0].toUpperCase()}</div>
                      <div><p className="font-medium text-gray-900 dark:text-gray-100">{u.name || '—'}</p><p className="text-xs text-gray-400">{u.email}</p></div>
                    </div>
                  </td>
                  <td className="p-4">
                    {editing === u.id ? (
                      <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm">
                        {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                    ) : (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${ROLE_COLOR[u.role] || ROLE_COLOR.viewer}`}>{ROLE_LABEL[u.role] || u.role}</span>
                    )}
                  </td>
                  <td className="p-4">
                    {editing === u.id ? (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Active</span>
                      </label>
                    ) : (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${u.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>{u.is_active ? 'Active' : 'Disabled'}</span>
                    )}
                  </td>
                  <td className="p-4 text-xs text-gray-500 dark:text-gray-400">{new Date(u.created_at * 1000).toLocaleDateString()}</td>
                  <td className="p-4 text-xs text-gray-500 dark:text-gray-400">{u.last_login ? new Date(u.last_login * 1000).toLocaleString() : 'Never'}</td>
                  <td className="p-4">
                    {editing === u.id ? (
                      <div className="flex items-center gap-2">
                        <input type="password" value={editForm.password} onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} placeholder="New pwd (opt)" className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm w-28" />
                        <button onClick={() => saveEdit(u.id)} disabled={saving} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"><Check size={15} /></button>
                        <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={15} /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => startEdit(u)} className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"><Edit size={15} /></button>
                        <button onClick={() => remove(u.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 size={15} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
